import React from 'react';

import { appConfig } from '../../../config/app-config';
import { formatDateRange as formatDateRangeUtil } from '../../../shared/utils/dateUtil';
import { usePlatformData } from '../../home/hooks/usePlatformData';
import type { CustomerGroup } from '../../home/types/home.types';
import type { ProductWithAddOns } from '../types/product.types';
import { getPassPriceInfo } from '../utils/passPriceUtil';

import PassCard from './pass/PassCard';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface ProductPassListProps {
  products?: ProductWithAddOns[];
  selectedPassId?: string | null;
  onPassSelect?: (product: ProductWithAddOns) => void;
  selectedRateId?: string | null;
  customerGroups?: CustomerGroup[];
  isEnabled?: boolean;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const ProductPassList: React.FC<ProductPassListProps> = ({
  products = [],
  selectedPassId,
  onPassSelect,
  selectedRateId,
  customerGroups,
  isEnabled = false,
}) => {
  const { eventData } = usePlatformData();
  const labels = eventData?.labels as Record<string, string> | undefined;

  /**
   * Safe helper to get attributes from either Product or ProductProjection structure.
   */
  const getAttribute = (product: ProductWithAddOns, attributeName: string): unknown => {
    const masterVariant = product?.masterVariant || (product as unknown as { masterData?: { current?: { masterVariant?: unknown } } })?.masterData?.current?.masterVariant;
    if (!(masterVariant as { attributes?: Array<{ name: string; value: unknown }> })?.attributes) return null;

    const attr = (masterVariant as { attributes: Array<{ name: string; value: unknown }> }).attributes.find(
      (a: { name: string }) => a.name === attributeName
    );
    return attr?.value ?? null;
  };

  const formatDateRange = (product: ProductWithAddOns) => {
    const startDate = getAttribute(product, 'startDate') as string | null;
    const endDate = getAttribute(product, 'endDate') as string | null;
    return formatDateRangeUtil(startDate, endDate);
  };

  const handlePassClick = (product: ProductWithAddOns) => {
    if (onPassSelect) {
      onPassSelect(product);
    }
  };

  if (!products || products.length === 0) {
    return <div className="p-8">No products found</div>;
  }

  // Helper to create a consistent sorter for the popular product
  const createPopularitySorter = (popularId?: string) => (a: ProductWithAddOns, b: ProductWithAddOns) => {
    return (popularId === b.id ? 1 : 0) - (popularId === a.id ? 1 : 0);
  };

  const sortedProducts = [...products].sort(createPopularitySorter(eventData?.popularProductId));

  return (
    <div className="w-full flex flex-col gap-3">
      {sortedProducts.map((product, index) => {
        const locale = appConfig.defaults.locale || 'en';
        const { displayPrice, originalPrice, discountName, savedAmount } = getPassPriceInfo(
          product,
          selectedRateId,
          customerGroups
        );

        const benefits = (getAttribute(product, 'benefits') as string[]) || [];
        const marketingTag = getAttribute(product, 'marketing-tag');

        const isPopular = eventData?.popularProductId === product.id;
        const isEarlyBird =
          !!discountName ||
          (marketingTag &&
            typeof marketingTag === 'string' &&
            marketingTag.toLowerCase().includes('early bird'));
        const isSelected = selectedPassId === product.id;
        const startDate = getAttribute(product, 'startDate') as string | undefined;
        const endDate = getAttribute(product, 'endDate') as string | undefined;

        return (
          <PassCard
            key={product.id}
            isFirst={index === 0}
            title={product.name?.[locale] || product.name?.['en'] || 'Untitled Pass'}
            dateRange={formatDateRange(product)}
            startDate={startDate}
            endDate={endDate}
            price={displayPrice}
            originalPrice={originalPrice}
            description={
              product.description?.[locale] ||
              product.description?.['en'] ||
              'No description available'
            }
            benefits={benefits}
            isActive={isSelected}
            isEnabled={isEnabled}
            onClick={() => handlePassClick(product)}
            passData={product}
            isEarlyBird={!!isEarlyBird}
            discountName={discountName}
            savedAmount={savedAmount}
            discountText={isEarlyBird ? (discountName || (marketingTag as string)) : null}
            whoForLabel={labels?.passSectionHeadingAudience || "Who's this pass for?"}
            accessLabel={labels?.passSectionHeadingBenefits || 'Get full access to:'}
            isPopular={!!isPopular}
          />
        );
      })}
    </div>
  );
};

export default ProductPassList;
